import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  keyframes,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import {
  CampaignSummaryItem,
  areAllCampaignsHealthy,
  getCampaignsNeedingAttention,
  getBorrowDisplayLabel,
  getCampaignHealthDisplay,
  getCampaignOperationsMetrics,
  getCanLendCount,
  getCoverageGivenLabel,
  getCoverageReceivedLabel,
  getCampaignCoverageActions,
  getRecommendedCampaignAction,
  sortCampaignsByOperationalPriority,
} from '../../utils/operationsDashboardUtils';
import {
  getCampaignHealthRowBg,
  getOperationalScoreHeatColor,
  getStaffingProgressColor,
  OPS_COLORS,
} from '../../constants/operationsDashboardColors';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';

const statusFlash = keyframes`
  0% { box-shadow: inset 0 0 0 2px rgba(198, 40, 40, 0.5); }
  70% { box-shadow: inset 0 0 0 2px rgba(198, 40, 40, 0); }
  100% { box-shadow: inset 0 0 0 2px rgba(198, 40, 40, 0); }
`;

interface CampaignOperationsBoardProps {
  campaigns: CampaignSummaryItem[];
  rows?: any[];
  dailyNotes?: any[];
  selectedCampaignId?: string;
  onSelectCampaign: (campaignId: string) => void;
  onAssignCoverage?: (campaignId: string) => void;
  onChangeCoverage?: (campaignId: string) => void;
  onRemoveCoverage?: (campaignId: string) => void;
  canManage?: boolean;
  density?: DashboardDensity;
}

function ActionLink({
  label,
  onClick,
  color,
}: {
  label: string;
  onClick: () => void;
  color: string;
}) {
  return (
    <Typography
      component="button"
      variant="caption"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        p: 0,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.68rem',
        color,
        textAlign: 'left',
        textDecoration: 'underline',
        textUnderlineOffset: 2,
        display: 'block',
        lineHeight: 1.35,
        '&:hover': { opacity: 0.85 },
      }}
    >
      {label}
    </Typography>
  );
}

const cellSx = (compact: boolean) => ({
  py: compact ? 0.5 : 0.75,
  px: compact ? 0.75 : 1,
  fontSize: compact ? '0.72rem' : '0.78rem',
  lineHeight: 1.35,
  verticalAlign: 'middle' as const,
  whiteSpace: 'nowrap' as const,
});

function HoverDetails({
  item,
  metrics,
  campaignRows,
}: {
  item: CampaignSummaryItem;
  metrics: ReturnType<typeof getCampaignOperationsMetrics>;
  campaignRows: any[];
}) {
  const transfers = campaignRows.filter((r) => r.coverageMode === 'FULL_TRANSFER' || r.isCoveringOthers);
  const shared = campaignRows.filter((r) => r.coverageMode === 'SHARED_COVERAGE');

  return (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
        {item.campaignName}
      </Typography>
      <Typography variant="caption" display="block">Scheduled: {item.scheduled}</Typography>
      <Typography variant="caption" display="block">Present (on time): {item.present}</Typography>
      <Typography variant="caption" display="block">Present (Late): {item.late}</Typography>
      <Typography variant="caption" display="block">Absent: {item.absent}</Typography>
      <Typography variant="caption" display="block">
        Covered: {metrics.hasPartialCoverage ? 'Partial Coverage' : metrics.coverageLabel}
      </Typography>
      {metrics.partialCoverageDetail && (
        <Typography variant="caption" display="block">{metrics.partialCoverageDetail}</Typography>
      )}
      <Typography variant="caption" display="block">
        Coverage Given: {getCoverageGivenLabel(item.coverageGiven ?? 0)}
      </Typography>
      <Typography variant="caption" display="block">
        Coverage Received: {getCoverageReceivedLabel(item.coverageReceived ?? 0)}
      </Typography>
      <Typography variant="caption" display="block">
        Temporary Transfers: {transfers.length > 0 ? transfers.map((r) => r.agentName).join(', ') : 'None'}
      </Typography>
      <Typography variant="caption" display="block">
        Shared Coverage: {shared.length > 0 ? shared.length : 'None'}
      </Typography>
      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 600 }}>
        Operational Readiness: {item.operationalScore}%
      </Typography>
    </Box>
  );
}

function ExpandedRowDetails({
  item,
  campaignRows,
  dailyNotes,
  compact,
  canManage,
  onChangeCoverage,
  onRemoveCoverage,
}: {
  item: CampaignSummaryItem;
  campaignRows: any[];
  dailyNotes: any[];
  compact: boolean;
  canManage?: boolean;
  onChangeCoverage?: (campaignId: string) => void;
  onRemoveCoverage?: (campaignId: string) => void;
}) {
  const scheduled = campaignRows.filter((r) => r.requiresCoverage !== false);
  const present = campaignRows.filter((r) => ['PRESENT', 'LATE', 'SLIGHTLY_LATE'].includes(r.attendanceStatus));
  const absent = campaignRows.filter((r) => ['ABSENT', 'NO_CALL_NO_SHOW', 'SCHEDULED_OFF'].includes(r.attendanceStatus));
  const coverageAssignments = campaignRows.filter((r) =>
    ['COVERED', 'PARTIALLY_COVERED'].includes(r.coverageStatus),
  );
  const campaignNotes = dailyNotes.filter((n) => !n.campaignId || n.campaignId === item.campaignId);

  const listBlock = (title: string, names: string[], empty: string) => (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="caption" component="div" sx={{ lineHeight: 1.5 }}>
        {names.length > 0 ? names.join(', ') : empty}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ px: compact ? 1 : 1.5, py: compact ? 0.75 : 1, bgcolor: 'background.default' }}>
      <Grid container spacing={compact ? 1 : 1.5}>
        <Grid item xs={12} sm={6} md={3}>
          {listBlock('Employees Scheduled', scheduled.map((r) => r.agentName), 'None scheduled')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {listBlock('Employees Present', present.map((r) => r.agentName), 'None present')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {listBlock('Employees Absent', absent.map((r) => r.agentName), 'None absent')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            Coverage Assignments
          </Typography>
          {coverageAssignments.length === 0 ? (
            <Typography variant="caption">No active coverage</Typography>
          ) : (
            coverageAssignments.map((r) => (
              <Box key={r.id} sx={{ mb: 0.5 }}>
                <Typography variant="caption" display="block" sx={{ lineHeight: 1.5 }}>
                  {r.agentName}
                  {r.coveredByName ? ` → covered by ${r.coveredByName}` : ''}
                  {r.coverageStatus === 'PARTIALLY_COVERED' ? ' (Partial)' : ''}
                </Typography>
                {canManage && r.coverageAssignmentId && (
                  <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                    {onChangeCoverage && (
                      <Typography
                        component="button"
                        variant="caption"
                        onClick={() => onChangeCoverage(item.campaignId)}
                        sx={{
                          p: 0,
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          color: OPS_COLORS.coverage,
                          textDecoration: 'underline',
                          fontSize: '0.62rem',
                        }}
                      >
                        Change
                      </Typography>
                    )}
                    {onRemoveCoverage && (
                      <Typography
                        component="button"
                        variant="caption"
                        onClick={() => onRemoveCoverage(item.campaignId)}
                        sx={{
                          p: 0,
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          color: OPS_COLORS.critical,
                          textDecoration: 'underline',
                          fontSize: '0.62rem',
                        }}
                      >
                        Remove
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>
            ))
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            Campaign Timeline
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {present.length} present · {absent.length} absent · {coverageAssignments.length} coverage assignment{coverageAssignments.length === 1 ? '' : 's'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.25 }}>
            Supervisor Notes
          </Typography>
          {campaignNotes.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No notes for this campaign</Typography>
          ) : (
            campaignNotes.slice(0, 3).map((n) => (
              <Typography key={n.id} variant="caption" display="block" sx={{ lineHeight: 1.5 }}>
                {n.note}
              </Typography>
            ))
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

function MetricCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <Box sx={{ textAlign: 'center', minWidth: 52 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.58rem', lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.78rem', color: color || 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

const CampaignOperationsBoard: React.FC<CampaignOperationsBoardProps> = ({
  campaigns,
  rows = [],
  dailyNotes = [],
  selectedCampaignId = '',
  onSelectCampaign,
  onAssignCoverage,
  onChangeCoverage,
  onRemoveCoverage,
  canManage = false,
  density = 'standard',
}) => {
  const compact = isCompact(density);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showHealthyCampaigns, setShowHealthyCampaigns] = useState(false);
  const prevHealthRef = useRef<Record<string, string>>({});
  const [flashingIds, setFlashingIds] = useState<Set<string>>(new Set());

  const sortedCampaigns = useMemo(
    () => sortCampaignsByOperationalPriority(campaigns),
    [campaigns],
  );

  const rowsByCampaign = useMemo(() => {
    const map = new Map<string, any[]>();
    rows.forEach((row) => {
      const list = map.get(row.campaignId) || [];
      list.push(row);
      map.set(row.campaignId, list);
    });
    return map;
  }, [rows]);

  const allHealthy = useMemo(() => areAllCampaignsHealthy(campaigns), [campaigns]);

  const displayCampaigns = useMemo(
    () => (allHealthy ? sortedCampaigns : getCampaignsNeedingAttention(sortedCampaigns)),
    [allHealthy, sortedCampaigns],
  );

  const hiddenHealthyCount = useMemo(
    () => (allHealthy ? 0 : sortedCampaigns.length - displayCampaigns.length),
    [allHealthy, sortedCampaigns.length, displayCampaigns.length],
  );

  const visibleCampaigns = useMemo(
    () => (allHealthy || showHealthyCampaigns ? sortedCampaigns : displayCampaigns),
    [allHealthy, showHealthyCampaigns, sortedCampaigns, displayCampaigns],
  );

  useEffect(() => {
    if (allHealthy) {
      setShowHealthyCampaigns(false);
    }
  }, [allHealthy]);

  useEffect(() => {
    const changed = new Set<string>();
    sortedCampaigns.forEach((c) => {
      const level = getCampaignHealthDisplay(c).level;
      const prev = prevHealthRef.current[c.campaignId];
      if (prev && prev !== level) changed.add(c.campaignId);
      prevHealthRef.current[c.campaignId] = level;
    });
    if (changed.size > 0) {
      setFlashingIds(changed);
      const timer = setTimeout(() => setFlashingIds(new Set()), 1400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [sortedCampaigns]);

  const handleRowSelect = (campaignId: string) => {
    const isSelected = selectedCampaignId === campaignId;
    onSelectCampaign(campaignId);
    setExpandedId((prev) => (isSelected ? null : campaignId));
  };

  if (campaigns.length === 0) return null;

  const headers = [
    '',
    'Status',
    'Campaign',
    'Staffing',
    'Required',
    'Working',
    'Coverage',
    'Still Needed',
    'Can Borrow',
    'Received',
    'Given',
    'Next Step',
    '',
  ];

  return (
    <Box sx={{ mb: compact ? 0.75 : 1 }}>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ fontSize: compact ? '0.85rem' : '0.95rem', lineHeight: 1.3 }}
      >
        📊 Campaign Operations Board
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={0.75}
        sx={{ mb: 0.75 }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: compact ? '0.68rem' : '0.72rem' }}
        >
          Real-time staffing health and operational readiness by campaign.
        </Typography>
        {!allHealthy && hiddenHealthyCount > 0 && (
          <Button
            size="small"
            variant={showHealthyCampaigns ? 'contained' : 'outlined'}
            onClick={() => setShowHealthyCampaigns((value) => !value)}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontSize: '0.72rem',
              py: 0.25,
              px: 1.25,
            }}
          >
            {showHealthyCampaigns
              ? 'Show only needs attention'
              : `Show ${hiddenHealthyCount} healthy campaign${hiddenHealthyCount === 1 ? '' : 's'}`}
          </Button>
        )}
      </Stack>

      {!allHealthy && !showHealthyCampaigns && hiddenHealthyCount > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.75, fontSize: compact ? '0.65rem' : '0.7rem' }}
        >
          Showing {displayCampaigns.length} campaign{displayCampaigns.length === 1 ? '' : 's'} needing attention.
          {' '}
          {hiddenHealthyCount} fully staffed campaign{hiddenHealthyCount === 1 ? ' is' : 's are'} hidden.
        </Typography>
      )}

      {allHealthy && (
        <Box
          sx={{
            py: 1.5,
            px: 2,
            mb: 1,
            textAlign: 'center',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: `${OPS_COLORS.excellent}14`,
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
            🟢 Operations Stable — All campaigns are fully staffed.
          </Typography>
        </Box>
      )}

      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflowX: 'auto',
        }}
      >
        <Table size="small" sx={{ minWidth: 1100, '& .MuiTableCell-root': cellSx(compact) }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              {headers.map((h) => (
                <TableCell key={h || 'expand'} sx={{ fontWeight: 700, fontSize: '0.68rem' }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCampaigns.map((item) => {
              const health = getCampaignHealthDisplay(item);
              const campaignRows = rowsByCampaign.get(item.campaignId) || [];
              const metrics = getCampaignOperationsMetrics(item, campaignRows);
              const canLend = getCanLendCount(item);
              const borrowLabel = getBorrowDisplayLabel(item, canLend);
              const coverageActions = getCampaignCoverageActions(campaignRows);
              const recommended = getRecommendedCampaignAction(item, campaigns, campaignRows);
              const progressColor = item.scheduled <= 0
                ? OPS_COLORS.scheduledOff
                : getStaffingProgressColor(metrics.staffingPercent);
              const rowBg = getCampaignHealthRowBg(health.level);
              const isSelected = selectedCampaignId === item.campaignId;
              const isExpanded = expandedId === item.campaignId;
              const isFlashing = flashingIds.has(item.campaignId);
              const hasUncovered = coverageActions.uncoveredRows.length > 0;

              return (
                <React.Fragment key={item.campaignId}>
                  <TableRow
                    hover
                    selected={isSelected}
                    onClick={() => handleRowSelect(item.campaignId)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: rowBg,
                      transition: 'background-color 0.4s ease',
                      animation: isFlashing ? `${statusFlash} 1.2s ease-out` : 'none',
                      borderLeft: `4px solid ${getOperationalScoreHeatColor(item.operationalScore)}`,
                      '&:hover': { filter: 'brightness(0.98)' },
                      '&.Mui-selected': {
                        bgcolor: `${OPS_COLORS.coverage}18`,
                        '&:hover': { bgcolor: `${OPS_COLORS.coverage}22` },
                      },
                    }}
                  >
                      <TableCell sx={{ width: 28, p: 0.25 }}>
                        <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>{health.emoji}</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: getOperationalScoreHeatColor(item.operationalScore), display: 'block' }}
                        >
                          {health.label}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ fontWeight: 700, maxWidth: 140 }}>
                        <Tooltip
                          title={<HoverDetails item={item} metrics={metrics} campaignRows={campaignRows} />}
                          arrow
                          placement="top"
                          enterDelay={400}
                        >
                          <Typography variant="inherit" noWrap title={item.campaignName}>
                            {item.campaignName}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell sx={{ minWidth: 160, whiteSpace: 'normal' }}>
                        {item.scheduled > 0 ? (
                          <Box sx={{ minWidth: 140 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                              <Typography variant="caption" fontWeight={700} sx={{ color: progressColor }}>
                                {metrics.staffingPercent}%
                              </Typography>
                              <Typography variant="caption" fontWeight={600} noWrap>
                                {metrics.workingLabel}
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={metrics.staffingPercent}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'action.selected',
                                transition: 'all 0.6s ease',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  bgcolor: progressColor,
                                  transition: 'transform 0.6s ease',
                                },
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography variant="caption" fontWeight={600} color="text.secondary" fontStyle="italic">
                            Not Scheduled Today
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <MetricCell label="Required" value={item.scheduled > 0 ? metrics.required : '—'} />
                      </TableCell>
                      <TableCell align="center">
                        <MetricCell label="Working" value={item.scheduled > 0 ? metrics.working : '—'} color={OPS_COLORS.present} />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'normal', maxWidth: 100 }}>
                        <MetricCell
                          label="Coverage"
                          value={item.scheduled > 0 ? metrics.coverageLabel : '—'}
                          color={OPS_COLORS.coverage}
                        />
                        {metrics.partialCoverageDetail && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem', display: 'block' }}>
                            {metrics.partialCoverageDetail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <MetricCell
                          label="Still Needed"
                          value={item.scheduled > 0 ? metrics.stillNeeded : '—'}
                          color={metrics.stillNeeded > 0 ? OPS_COLORS.uncovered : OPS_COLORS.present}
                        />
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'normal', maxWidth: 90 }}>
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.68rem' }}>
                          {item.scheduled > 0 ? borrowLabel : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="caption" fontWeight={600}>
                          {item.scheduled > 0 ? getCoverageReceivedLabel(item.coverageReceived ?? 0) : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="caption" fontWeight={600}>
                          {item.scheduled > 0 ? getCoverageGivenLabel(item.coverageGiven ?? 0) : '—'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ maxWidth: 120, whiteSpace: 'normal' }}>
                        <Stack spacing={0.2} onClick={(e) => e.stopPropagation()}>
                          {canManage && coverageActions.hasActiveCoverage && onChangeCoverage && (
                            <ActionLink
                              label="Change Coverage"
                              onClick={() => onChangeCoverage(item.campaignId)}
                              color={OPS_COLORS.coverage}
                            />
                          )}
                          {canManage && coverageActions.hasActiveCoverage && onRemoveCoverage && (
                            <ActionLink
                              label="Remove Coverage"
                              onClick={() => onRemoveCoverage(item.campaignId)}
                              color={OPS_COLORS.critical}
                            />
                          )}
                          {canManage && hasUncovered && onAssignCoverage && (
                            <ActionLink
                              label="Assign Coverage"
                              onClick={() => onAssignCoverage(item.campaignId)}
                              color={OPS_COLORS.uncovered}
                            />
                          )}
                          {canManage
                            && !hasUncovered
                            && metrics.hasPartialCoverage
                            && onAssignCoverage && (
                            <ActionLink
                              label="Add Coverage"
                              onClick={() => onAssignCoverage(item.campaignId)}
                              color={OPS_COLORS.uncovered}
                            />
                          )}
                          {!canManage || (
                            !coverageActions.hasActiveCoverage
                            && !hasUncovered
                            && !metrics.hasPartialCoverage
                            && !recommended.actionable
                          ) ? (
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{
                                fontSize: '0.68rem',
                                color: ['Fully Staffed', 'Coverage Active'].includes(recommended.label)
                                  ? OPS_COLORS.present
                                  : 'text.secondary',
                              }}
                            >
                              {recommended.label}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ width: 32, p: 0.25 }}>
                        <IconButton
                          size="small"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedId(isExpanded ? null : item.campaignId);
                          }}
                        >
                          {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>

                  <TableRow>
                    <TableCell colSpan={headers.length} sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <ExpandedRowDetails
                          item={item}
                          campaignRows={campaignRows}
                          dailyNotes={dailyNotes}
                          compact={compact}
                          canManage={canManage}
                          onChangeCoverage={onChangeCoverage}
                          onRemoveCoverage={onRemoveCoverage}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CampaignOperationsBoard;
